import React from "react";
import {
    Box,
    Grid,
    Typography,
    Paper,
    alpha,
} from "@mui/material";

import {
    generateCalendarDays,
    formatDateToYYYYMMDD,
    isToday,
    isBetween,
    getWeekDays,
    buildMonths,
} from "./utils";

const Calendar = ({
    currentMonth,
    monthsToShow = 2,
    startDate,
    endDate,
    hoverDate,
    activeInput,
    onDateClick,
    onHover,
    theme,
    isArabic,
}) => {
    const months = buildMonths(
        currentMonth,
        monthsToShow
    );

    const weekDays = getWeekDays(isArabic);

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${monthsToShow}, 1fr)`,
                gap: 3,
            }}
        >
            {months.map((month) => {
                const days =
                    generateCalendarDays(month);

                return (
                    <Paper
                        key={month.toISOString()}
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.2
                            )}`,
                        }}
                    >
                        {/* Month Title */}
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            textAlign="center"
                            sx={{ mb: 2 }}
                        >
                            {month.toLocaleDateString(
                                isArabic
                                    ? "ar-SA"
                                    : "en-US",
                                {
                                    month: "long",
                                    year: "numeric",
                                }
                            )}
                        </Typography>

                        {/* Week Days */}
                        <Grid container spacing={0.5}>
                            {weekDays.map(
                                (day, index) => (
                                    <Grid
                                        item
                                        xs={12 / 7}
                                        key={index}
                                    >
                                        <Box
                                            sx={{
                                                textAlign:
                                                    "center",
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color:
                                                    "text.secondary",
                                            }}
                                        >
                                            {day}
                                        </Box>
                                    </Grid>
                                )
                            )}
                        </Grid>

                        {/* Days */}
                        <Grid container spacing={0.5}>
                            {days.map((date, index) => {
                                if (!date) {
                                    return (
                                        <Grid
                                            item
                                            xs={12 / 7}
                                            key={index}
                                        >
                                            <Box
                                                sx={{
                                                    height: 36,
                                                }}
                                            />
                                        </Grid>
                                    );
                                }

                                const dateStr =
                                    formatDateToYYYYMMDD(
                                        date
                                    );

                                const today =
                                    isToday(date);

                                const isStart =
                                    dateStr ===
                                    startDate;

                                const isEnd =
                                    dateStr ===
                                    endDate;

                                const inRange =
                                    isBetween(
                                        date,
                                        startDate,
                                        endDate
                                    );

                                const isDisabled =
                                    activeInput ===
                                    "end" &&
                                    startDate &&
                                    dateStr < startDate;

                                return (
                                    <Grid
                                        item
                                        xs={12 / 7}
                                        key={index}
                                    >
                                        <Box
                                            onClick={() =>
                                                !isDisabled &&
                                                onDateClick(
                                                    date
                                                )
                                            }
                                            onMouseEnter={() =>
                                                onHover(dateStr)
                                            }
                                            onMouseLeave={() =>
                                                onHover(null)
                                            }
                                            sx={{
                                                height: 36,
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                cursor: isDisabled
                                                    ? "not-allowed"
                                                    : "pointer",

                                                fontSize:
                                                    today
                                                        ? 14
                                                        : 13,

                                                fontWeight:
                                                    today
                                                        ? 800
                                                        : isStart ||
                                                            isEnd
                                                            ? 700
                                                            : 400,

                                                color: isDisabled
                                                    ? alpha(
                                                        theme
                                                            .palette
                                                            .text
                                                            .primary,
                                                        0.3
                                                    )
                                                    : today
                                                        ? theme
                                                            .palette
                                                            .success
                                                            .main
                                                        : isStart ||
                                                            isEnd
                                                            ? "#fff"
                                                            : inRange
                                                                ? theme
                                                                    .palette
                                                                    .primary
                                                                    .main
                                                                : "text.primary",

                                                bgcolor:
                                                    isStart
                                                        ? theme
                                                            .palette
                                                            .primary
                                                            .main
                                                        : isEnd
                                                            ? theme
                                                                .palette
                                                                .secondary
                                                                .main
                                                            : inRange
                                                                ? alpha(
                                                                    theme
                                                                        .palette
                                                                        .primary
                                                                        .main,
                                                                    0.15
                                                                )
                                                                : "transparent",

                                                borderRadius:
                                                    isStart ||
                                                        isEnd
                                                        ? "25px"
                                                        : 1,

                                                border:
                                                    today &&
                                                        !isStart &&
                                                        !isEnd
                                                        ? `2px solid ${theme.palette.success.main}`
                                                        : "none",

                                                opacity:
                                                    isDisabled
                                                        ? 0.4
                                                        : 1,

                                                transition:
                                                    "0.2s",

                                                "&:hover":
                                                    !isDisabled && {
                                                        transform:
                                                            "scale(1.05)",
                                                        backgroundColor:
                                                            alpha(
                                                                theme
                                                                    .palette
                                                                    .primary
                                                                    .main,
                                                                0.2
                                                            ),
                                                    },
                                            }}
                                        >
                                            {date.getDate()}
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Paper>
                );
            })}
        </Box>
    );
};

export default Calendar;